import React, { useEffect, useMemo, useState } from "react";
import { signInWithPopup } from "firebase/auth";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  CssBaseline,
  Divider,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import GoogleIcon from "@mui/icons-material/Google";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import { auth, googleProvider } from "../firebase";
import { darkTheme } from "../ThemeContext";
import Starfield from "./Starfield";

// Login is a deliberately fixed-dark hero, independent of the app's
// light/dark toggle (which only affects the authenticated shell, and isn't
// visible from this screen anyway) — so it always reads from darkTheme
// rather than the ambient useTheme(), which could resolve to the light
// palette if that was the mode last active before logging out.
const accent = darkTheme.palette.primary.main;
const accentDark = darkTheme.palette.primary.dark;

function Login({ onLogin }) {
  const [typedText, setTypedText] = useState("");
  const fullText = "AutoLogg";

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      setTypedText(fullText);
      return;
    }
    let i = 0;
    const id = setInterval(() => {
      i++;
      setTypedText(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(id);
    }, 60);
    return () => clearInterval(id);
  }, [prefersReducedMotion]);

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      onLogin(result.user);
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  const features = [
    { icon: <BuildRoundedIcon fontSize="small" />, title: "Service logs", desc: "Oil, brakes, tires — all tracked." },
    { icon: <TimelineRoundedIcon fontSize="small" />, title: "Timeline view", desc: "See your maintenance story." },
    { icon: <AutoGraphRoundedIcon fontSize="small" />, title: "Mileage clarity", desc: "Know what’s due and when." },
  ];

  return (
    <>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          px: { xs: 2, md: 4 },
          background: `
            radial-gradient(900px circle at 18% 30%, ${alpha(accent, 0.10)}, transparent 58%),
            radial-gradient(700px circle at 85% 70%, ${alpha(accent, 0.06)}, transparent 60%),
            linear-gradient(180deg, #0D0E11, #101114)
          `,

          // Soft animated glow
          ...(prefersReducedMotion
            ? {}
            : {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: "-25%",
                  background: `
                    radial-gradient(520px circle at 20% 25%, ${alpha(accent, 0.12)}, transparent 62%),
                    radial-gradient(560px circle at 75% 65%, ${alpha(accent, 0.08)}, transparent 60%),
                    radial-gradient(460px circle at 55% 85%, rgba(255,255,255,0.04), transparent 62%)
                  `,
                  filter: "blur(20px)",
                  opacity: 0.85,
                  animation: "glowFloat 14s ease-in-out infinite alternate",
                  pointerEvents: "none",
                },
              }),

          "@keyframes glowFloat": {
            "0%": { transform: "translate3d(-1%, -1%, 0) rotate(-1deg)" },
            "100%": { transform: "translate3d(2%, 2%, 0) rotate(1deg)" },
          },

          "@keyframes shimmer": {
            "0%": { transform: "translateX(-140%)" },
            "100%": { transform: "translateX(140%)" },
          },
        }}
      >
        <Starfield />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
              gap: { xs: 3, md: 4 },
              alignItems: "center",
            }}
          >
            {/* Hero */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    backgroundColor: alpha(accent, 0.14),
                    border: `1px solid ${alpha(accent, 0.32)}`,
                    boxShadow: "0 14px 40px rgba(0,0,0,0.55)",
                  }}
                >
                  <DirectionsCarRoundedIcon sx={{ color: accent }} />
                </Avatar>

                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: "rgba(255,255,255,0.55)", letterSpacing: 1.6 }}
                  >
                    Maintenance made clean
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.05,
                      letterSpacing: -0.6,
                      background: `linear-gradient(90deg, rgba(255,255,255,0.95), ${accent})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {typedText || " "}
                  </Typography>
                </Box>
              </Stack>

              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255,255,255,0.78)",
                  fontWeight: 650,
                  maxWidth: 560,
                  mb: 3,
                }}
              >
                Track mileage, log services, and keep your car history organized —{" "}
                <Box component="span" sx={{ color: accent, fontWeight: 900 }}>
                  without the chaos.
                </Box>
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.2,
                  maxWidth: 620,
                }}
              >
                {features.map((f) => (
                  <Paper
                    key={f.title}
                    elevation={0}
                    sx={{
                      p: 1.6,
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.03)",
                      border: `1px solid ${alpha(accent, 0.14)}`,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                      <Box sx={{ color: accent, display: "grid", placeItems: "center" }}>{f.icon}</Box>
                      <Typography sx={{ fontWeight: 900, color: "rgba(255,255,255,0.92)" }}>
                        {f.title}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.68)" }}>
                      {f.desc}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Typography
                variant="caption"
                sx={{ display: "block", mt: 2.5, color: "rgba(255,255,255,0.50)" }}
              >
                Built for daily drivers, builds, and everything in between.
              </Typography>
            </Box>

            {/* RIGHT: Login Card*/}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                backgroundColor: darkTheme.palette.background.paper,
                border: `1px solid ${alpha(accent, 0.22)}`,
                boxShadow: "0 22px 80px rgba(0,0,0,0.55)",
                position: "relative",
              }}
            >
              {/* Top accent strip */}
              <Box
                sx={{
                  height: 4,
                  background: `linear-gradient(90deg,
                    ${alpha(accent, 0.15)},
                    ${accent},
                    rgba(255,255,255,0.10),
                    ${accent},
                    ${alpha(accent, 0.15)}
                  )`,
                }}
              />

              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: "rgba(255,255,255,0.92)", mb: 0.8 }}
                >
                  Sign in
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.62)", mb: 2.6 }}>
                  Your logs stay private. Sign in to view and manage your vehicles.
                </Typography>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 2.6 }} />

                <Button
                  onClick={handleGoogleSignIn}
                  variant="contained"
                  fullWidth
                  startIcon={<GoogleIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 900,
                    py: 1.35,
                    borderRadius: 2,
                    backgroundColor: accent,
                    color: darkTheme.palette.primary.contrastText,
                    boxShadow: "none",
                    position: "relative",
                    overflow: "hidden",
                    "&:hover": { backgroundColor: accentDark, boxShadow: "none" },
                    transition: "background-color 160ms ease",
                    "&::before": prefersReducedMotion
                      ? {}
                      : {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "45%",
                          height: "100%",
                          background:
                            "linear-gradient(90deg, rgba(255,255,255,0.0), rgba(255,255,255,0.22), rgba(255,255,255,0.0))",
                          transform: "translateX(-140%)",
                          animation: "shimmer 2.9s ease-in-out infinite",
                          pointerEvents: "none",
                        },
                  }}
                >
                  Continue with Google
                </Button>

                {/* “Preview” panel */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.03)",
                    border: `1px solid ${alpha(accent, 0.14)}`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Stack spacing={1.2} sx={{ position: "relative" }}>
                    <Typography sx={{ fontWeight: 900, color: "rgba(255,255,255,0.88)" }}>
                      What you’ll get
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: 99,
                          backgroundColor: accent,
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)" }}>
                        Quick add service entries (date + mileage)
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: 99,
                          backgroundColor: "rgba(255,255,255,0.55)",
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)" }}>
                        Timeline that’s actually readable
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: 99,
                          backgroundColor: alpha(accent, 0.55),
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)" }}>
                        Multiple vehicles under one account
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                <Typography
                  variant="caption"
                  align="center"
                  sx={{ display: "block", mt: 2.2, color: "rgba(255,255,255,0.48)" }}
                >
                  No spam. No data selling. Just your car’s history, safe and sound.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>
    </>
  );
}

export default Login;
